import React from 'react';
import type { DashboardModel } from '../../hooks/useDashboard';

type FleetManifestProps = DashboardModel & {
  compact?: boolean;
  onToggleCompact?: () => void;
};

/** Convert raw age string like "3561m" → "2d 11h" / "47m ago" / "Just now" */
const formatRelativeAge = (ageStr: string): string => {
  if (!ageStr || ageStr === '--') return '--';
  const mins = parseInt(ageStr, 10);
  if (!Number.isFinite(mins)) return ageStr;
  if (mins <= 0) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m ago` : `${h}h ago`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h ago` : `${d}d ago`;
};

const DashboardFleetManifest: React.FC<FleetManifestProps> = ({
  dark,
  liveAircrafts,
  aircraftFlightInfo,
  selectedAircraftLocation,
  selectedAircraft,
  activeTab,
  searchQuery,
  formatAgeMinutes,
  formatTime,
  mapRef,
  setSelectedAircraft,
  setSearchQuery,
  compact = false,
  onToggleCompact,
}) => {
  const panelClass = `xl:col-span-1 rounded-[2rem] border flex flex-col overflow-hidden h-full ${
    dark
      ? `${activeTab === 'history' ? 'bg-slate-900/66' : 'bg-slate-900/82'} backdrop-blur-xl border-white/12 shadow-[0_32px_72px_rgba(2,6,23,0.80)]`
      : `${activeTab === 'history' ? 'bg-white/76' : 'bg-white/95'} backdrop-blur-lg border-slate-200/70 shadow-[0_8px_32px_rgba(15,23,42,0.12)]`
  }`;

  // ── COMPACT ICON RAIL ─────────────────────────────────────────────────────
  if (compact) {
    return (
      <aside className={panelClass}>
        {/* Header: fleet icon + expand toggle */}
        <div className={`flex flex-col items-center gap-3 pt-4 pb-3 px-2 border-b ${
          dark
            ? 'border-white/8 bg-gradient-to-b from-slate-800/80 to-slate-800/40'
            : 'border-slate-200/80 bg-gradient-to-b from-slate-50 to-white'
        }`}>
          {/* Fleet icon badge */}
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${
            dark
              ? 'bg-amber-500/10 border-amber-400/20'
              : 'bg-indigo-50 border-indigo-200/60'
          }`}>
            <svg className={`w-4 h-4 ${dark ? 'text-amber-400' : 'text-indigo-500'}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
            </svg>
          </div>

          {/* Expand chevron */}
          <button
            type="button"
            onClick={onToggleCompact}
            title="Expand fleet manifest"
            className={`w-7 h-7 rounded-lg flex items-center justify-center border transition-all ${
              dark
                ? 'border-white/10 text-slate-500 hover:text-slate-200 hover:bg-white/10 hover:border-white/20'
                : 'border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100 hover:border-slate-300'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Aircraft cards */}
        <div className="flex-1 overflow-y-auto py-3 px-2 flex flex-col gap-2 custom-scrollbar">
          {liveAircrafts.map((ac) => {
            const isSelected = selectedAircraft?.id === ac.id;
            const last = ac.telemetry?.lastUpdate || undefined;
            const age = formatAgeMinutes(last);
            const ageMins = age !== '--' ? parseInt(age, 10) : NaN;
            const isStale = Number.isFinite(ageMins) && ageMins >= 15;
            const isVeryFresh = Number.isFinite(ageMins) && ageMins <= 2;
            const isFlying = (ac.telemetry?.groundSpeed || 0) > 30;
            const shortTail = ac.tailNumber.replace('5H-', '');

            const dotColorClass = !last
              ? 'bg-slate-400'
              : isStale
              ? 'bg-amber-500'
              : isVeryFresh
              ? 'bg-emerald-400'
              : 'bg-emerald-500';

            const accentColor = isStale ? 'bg-amber-500' : isVeryFresh ? 'bg-emerald-400' : 'bg-emerald-500';

            return (
              <button
                key={ac.id}
                type="button"
                title={`${ac.tailNumber} — ${ac.name || 'Unknown'}`}
                onClick={() => {
                  setSelectedAircraft(ac);
                  if (ac.telemetry?.lat && ac.telemetry?.lng && mapRef.current) {
                    mapRef.current.flyTo({ center: [ac.telemetry.lng, ac.telemetry.lat], zoom: 15, duration: 1500 });
                  }
                }}
                className={`w-full rounded-2xl border flex flex-col items-center gap-2 pt-2.5 pb-3 relative overflow-hidden transition-all duration-200 cursor-pointer ${
                  isSelected
                    ? dark
                      ? 'bg-amber-500/15 border-amber-400/45 shadow-[0_6px_24px_rgba(245,158,11,0.22)]'
                      : 'bg-indigo-50/95 border-indigo-300/70 shadow-[0_6px_18px_rgba(99,102,241,0.18)]'
                    : dark
                      ? 'bg-slate-800/55 border-white/8 hover:bg-slate-700/65 hover:border-white/16 hover:shadow-[0_4px_12px_rgba(0,0,0,0.3)]'
                      : 'bg-white/80 border-slate-200/70 hover:bg-white hover:border-slate-300 hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)]'
                }`}
              >
                {/* Top accent line */}
                <div className={`absolute top-0 left-3 right-3 h-[2px] rounded-full ${
                  isSelected
                    ? dark ? 'bg-amber-400' : 'bg-indigo-400'
                    : `${accentColor} opacity-40`
                }`} />

                {/* Airplane icon */}
                <svg
                  className={`w-4 h-4 transition-all duration-300 ${
                    isSelected
                      ? dark ? 'text-amber-300' : 'text-indigo-500'
                      : dark ? 'text-slate-400' : 'text-slate-400'
                  } ${isFlying ? '-rotate-45' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                </svg>

                {/* Tail abbreviation */}
                <span className={`text-[10px] font-black tracking-wider leading-none ${
                  isSelected
                    ? dark ? 'text-amber-200' : 'text-indigo-700'
                    : dark ? 'text-slate-200' : 'text-slate-600'
                }`}>{shortTail}</span>

                {/* GPS status dot */}
                <div className={`w-1.5 h-1.5 rounded-full ${dotColorClass} ${isVeryFresh ? 'animate-pulse' : ''}`} />
              </button>
            );
          })}
        </div>
      </aside>
    );
  }

  // ── FULL PANEL ────────────────────────────────────────────────────────────
  return (
    <aside className={panelClass}>
      <div className={`${activeTab === 'history' ? 'p-4 gap-2.5' : 'p-5 gap-3'} border-b flex flex-col ${dark ? 'border-white/8 bg-gradient-to-b from-slate-800/80 to-slate-800/40' : 'border-slate-200/80 bg-gradient-to-b from-slate-50 to-white'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-[10px] font-black uppercase tracking-widest ${dark ? 'text-amber-300' : 'text-slate-600'}`}>Fleet Manifest</h3>
          {onToggleCompact && (
            <button
              type="button"
              onClick={onToggleCompact}
              title="Collapse to icon rail"
              className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                dark ? 'text-slate-500 hover:text-slate-200 hover:bg-white/8' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              {/* Chevron-left icon */}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>
        <input
          type="text"
          placeholder="Filter Assets..."
          className={`w-full rounded-xl px-4 py-2 text-[10px] outline-none focus:ring-2 ${
            dark ? 'bg-slate-700 border border-amber-400/30 text-amber-200 focus:ring-amber-400/40' : 'bg-white border border-slate-200 text-slate-800 focus:ring-indigo-400/40'
          }`}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-3 space-y-2">
          {liveAircrafts
            .filter((a) => a.tailNumber.toLowerCase().includes(searchQuery.toLowerCase()))
            .map((ac) => {
              const isSelected = selectedAircraft?.id === ac.id;
              const last = ac.telemetry?.lastUpdate || undefined;
              const age = formatAgeMinutes(last);
              const ageMins = age !== '--' ? parseInt(age, 10) : NaN;
              const isStale = Number.isFinite(ageMins) && ageMins >= 15;
              const isVeryFresh = Number.isFinite(ageMins) && ageMins <= 2;

              const statusShort =
                ac.status === 'AVAILABLE' || ac.status === 'SERVICEABLE' || ac.status === 'IN_FLIGHT'
                  ? 'READY'
                  : 'MAINT';

              const speed = ac.telemetry?.groundSpeed || 0;
              const altitude = ac.telemetry?.altitude || 0;
              const flightInfo = aircraftFlightInfo[ac.tailNumber] || {};
              const locationText =
                flightInfo.lastLocation ||
                (isSelected ? selectedAircraftLocation || undefined : undefined) ||
                ac.name ||
                'Unknown';

              // GPS freshness dot color
              const gpsDotColor = !last
                ? 'bg-slate-400'
                : isStale
                ? 'bg-amber-500'
                : isVeryFresh
                ? 'bg-emerald-400 animate-pulse'
                : 'bg-emerald-500';

              return (
                <button
                  key={ac.id}
                  onClick={() => {
                    setSelectedAircraft(ac);
                    if (ac.telemetry && ac.telemetry.lat && ac.telemetry.lng && mapRef.current) {
                      mapRef.current.flyTo({
                        center: [ac.telemetry.lng, ac.telemetry.lat],
                        zoom: 15,
                        duration: 1500,
                      });
                    }
                  }}
                  className={`w-full text-left rounded-2xl border px-3 py-3 transition-all ${
                    isSelected
                      ? dark
                        ? 'bg-amber-500/12 border-amber-400/40 shadow-[0_8px_22px_rgba(245,158,11,0.16)]'
                        : 'bg-indigo-50/90 border-indigo-300/70 shadow-[0_8px_20px_rgba(99,102,241,0.16)]'
                      : dark
                        ? 'bg-slate-800/70 border-white/10 hover:bg-slate-800/95'
                        : 'bg-white/80 border-slate-200/80 hover:bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${gpsDotColor}`} />
                        <p className={`text-[12px] font-black truncate ${dark ? 'text-white' : 'text-slate-900'}`}>{ac.tailNumber}</p>
                      </div>
                      <p className={`mt-1 text-[10px] truncate ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
                        {locationText}
                      </p>
                      {/* GPS freshness — relative time only, no raw timestamp */}
                      <p className={`mt-1 text-[9px] font-semibold ${isStale ? 'text-amber-500' : dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        {!last ? 'No GPS' : isVeryFresh ? 'GPS · Live' : `GPS · ${formatRelativeAge(age)}`}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase ${
                          statusShort === 'READY'
                            ? dark
                              ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/25'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : dark
                              ? 'bg-rose-500/15 text-rose-300 border border-rose-400/25'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}
                      >
                        {statusShort}
                      </span>
                      {/* Last GPS time — small mono below badge */}
                      <p className={`mt-1 text-[9px] font-mono ${dark ? 'text-slate-400' : 'text-slate-400'}`}>
                        {last ? new Date(last).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '--'}
                      </p>
                    </div>
                  </div>

                  {isSelected && (
                    <div className={`mt-2 pt-2 border-t grid grid-cols-2 gap-x-3 gap-y-1 ${dark ? 'border-white/10' : 'border-slate-200'}`}>
                      <p className={`text-[9px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>Pilot: <span className={dark ? 'text-amber-300' : 'text-indigo-700'}>{flightInfo.pilotName || '—'}</span></p>
                      <p className={`text-[9px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>GS: <span className={dark ? 'text-emerald-300' : 'text-emerald-700'}>{speed > 0 ? `${Math.round(speed)} kt` : '—'}</span></p>
                      <p className={`text-[9px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>ALT: <span className={dark ? 'text-cyan-300' : 'text-cyan-700'}>{altitude > 0 ? `${Math.round(altitude).toLocaleString()} ft` : '—'}</span></p>
                      <p className={`text-[9px] truncate ${dark ? 'text-slate-400' : 'text-slate-500'}`}>Loc: <span className={dark ? 'text-slate-200' : 'text-slate-700'}>{locationText}</span></p>
                    </div>
                  )}
                </button>
              );
            })}
          {liveAircrafts.filter((a) => a.tailNumber.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
            <div className={`px-3 py-8 text-center text-[10px] font-bold uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              No aircraft match filter
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default DashboardFleetManifest;
